import React from 'react';
import { Field } from 'redux-form';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { getCountries } from '@app/actions/accountActions';
import colors from '@app/constants/colors';

import TextInput from './Inputs/TextInput';
import CheckBox from './Inputs/CheckBox';
import RecaptchaComponent from './Inputs/Recaptcha';

import {
  isRequired, isRecaptchaRequired, isEmail, areEmailsMatch, isValidPhone, isValidPassword, arePasswordsMatch,
} from './FieldsValidationHelper';

const colorPropTypes = {
  fieldColor: PropTypes.string,
  textColor: PropTypes.string,
  backgroundColor: PropTypes.string,
};

const defaultColorProps = {
  fieldColor: 'white',
  textColor: 'white',
  backgroundColor: 'none',
};

export const Login = () => (
  <Field
    name="email"
    component={TextInput}
    type="text"
    attrs={{ autoComplete: 'email' }}
    validate={isRequired}
    required
    labelId="signIn.login"
  />
);

export const Password = ({ withValidation, ...rest }) => (
  <Field
    name="password"
    component={TextInput}
    type="password"
    attrs={{ autoComplete: withValidation ? 'new-password' : 'current-password' }}
    validate={withValidation ? [isRequired, isValidPassword] : isRequired}
    required
    labelId="signIn.password"
    {...rest}
  />
);

Password.propTypes = {
  ...colorPropTypes,
  isAccountCreation: PropTypes.bool,
};
Password.defaultProps = {
  ...defaultColorProps,
  isAccountCreation: false,
};

export const FirstName = props => (
  <Field
    name="firstName"
    component={TextInput}
    type="text"
    validate={isRequired}
    required
    labelId="personalInformation.first"
    {...props}
  />
);

FirstName.propTypes = colorPropTypes;
FirstName.defaultProps = defaultColorProps;

export const LastName = props => (
  <Field
    name="lastName"
    component={TextInput}
    type="text"
    validate={isRequired}
    required
    labelId="personalInformation.last"
    {...props}
  />
);

LastName.propTypes = colorPropTypes;
LastName.defaultProps = defaultColorProps;

export const PhoneNumber = props => (
  <Field
    name="phoneNumber"
    component={TextInput}
    type="text"
    validate={isValidPhone}
    labelId="personalInformation.phoneNumber"
    {...props}
  />
);

PhoneNumber.propTypes = colorPropTypes;
PhoneNumber.defaultProps = defaultColorProps;

class CountryComponent extends React.Component {
  componentDidMount() {
    const { getCountries } = this.props;
    getCountries();
  }

  render() {
    const { intl, countries = [] } = this.props;

    const options = [
      {
        data: intl.formatMessage({ id: 'selectCountryPlaceHolder' }),
        value: '',
      },
      ...countries,
    ];

    return (
      <Field
        name="addressCountry"
        component={TextInput}
        type="select"
        labelId="personalInformation.country"
        options={options}
        validate={isRequired}
        required
        {...this.props}
      />
    );
  }
}

CountryComponent.propTypes = {
  getCountries: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    data: PropTypes.string.isRequired,
  })).isRequired,
};

const mapStateToProps = ({ account: { countries } }) => ({
  countries,
});

export const Country = connect(mapStateToProps, { getCountries })(injectIntl(CountryComponent));

Country.propTypes = colorPropTypes;
Country.defaultProps = defaultColorProps;

export const LanguageSelection = injectIntl(({ intl, ...rest }) => {
  const options = [
    {
      data: intl.formatMessage({ id: 'myAccount.languagePlaceholder' }),
      value: '',
    },
    {
      data: 'English',
      value: 'eng',
    },
    {
      data: 'عربي',
      value: 'ara',
    },
  ];

  return (
    <Field
      name="languageId"
      component={TextInput}
      type="select"
      labelId="myAccount.language"
      options={options}
      validate={isRequired}
      required
      {...rest}
    />
  );
});

export const UserName = ({ isReadOnly, ...props }) => (
  <Field
    name="username"
    component={TextInput}
    type="text"
    validate={isReadOnly ? null : isRequired}
    required={!isReadOnly}
    labelId="personalInformation.userName"
    attrs={{ readOnly: isReadOnly }}
    {...props}
  />
);

UserName.propTypes = {
  ...colorPropTypes,
  isReadOnly: PropTypes.bool,
};

UserName.defaultProps = {
  ...defaultColorProps,
  isReadOnly: false,
};

export const Email = ({ isReadOnly, ...props }) => (
  <Field
    name="email"
    component={TextInput}
    type="email"
    validate={isReadOnly ? null : [isRequired, isEmail]}
    required={!isReadOnly}
    labelId="personalInformation.email"
    attrs={{ autoComplete: 'email', readOnly: isReadOnly }}
    {...props}
  />
);

Email.propTypes = {
  ...colorPropTypes,
  isReadOnly: PropTypes.bool,
};

Email.defaultProps = {
  ...defaultColorProps,
  isReadOnly: false,
};

export const ConfirmEmail = props => (
  <Field
    name="emailConfirm"
    component={TextInput}
    type="email"
    validate={[isRequired, areEmailsMatch]}
    required
    labelId="personalInformation.emailConfirm"
    {...props}
  />
);

ConfirmEmail.propTypes = colorPropTypes;
ConfirmEmail.defaultProps = defaultColorProps;

export const ConfirmPassword = props => (
  <Field
    name="passwordConfirm"
    component={TextInput}
    type="password"
    validate={[isRequired, arePasswordsMatch]}
    required
    labelId="personalInformation.passwordConfirm"
    {...props}
  />
);

ConfirmPassword.propTypes = colorPropTypes;
ConfirmPassword.defaultProps = defaultColorProps;

export const City = props => (
  <Field
    name="addressCity"
    component={TextInput}
    type="text"
    validate={isRequired}
    required
    labelId="myAccount.city"
    {...props}
  />
);

City.propTypes = colorPropTypes;
City.defaultProps = defaultColorProps;

export const Street = props => (
  <Field
    name="addressStreet"
    component={TextInput}
    type="text"
    validate={isRequired}
    required
    labelId="myAccount.street"
    {...props}
  />
);

Street.propTypes = colorPropTypes;
Street.defaultProps = defaultColorProps;

export const Zip = props => (
  <Field
    name="addressZip"
    component={TextInput}
    type="text"
    validate={isRequired}
    required
    labelId="myAccount.zip"
    {...props}
  />
);

Zip.propTypes = colorPropTypes;
Zip.defaultProps = defaultColorProps;

export const AcceptTerms = () => (
  <Field
    name="acceptTerms"
    component={CheckBox}
    labelId="personalInformation.acceptTerms"
    validate={isRequired}
    required
  />
);

export const AllowNews = () => (
  <Field
    name="allowNews"
    component={CheckBox}
    labelId="personalInformation.allowNews"
  />
);

export const ShareEmail = () => (
  <Field
    name="shareEmail"
    component={CheckBox}
    labelId="personalInformation.shareEmail"
  />
);

const RecaptchaField = ({ isReCaptureValid }) => (
  <Field
    name="recaptcha"
    component={RecaptchaComponent}
    validate={() => isRecaptchaRequired(isReCaptureValid)}
    required
  />
);

RecaptchaField.propTypes = {
  isReCaptureValid: PropTypes.bool.isRequired,
};

const RecaptchaMapStateToProps = ({ recaptcha: { isReCaptureValid } }) => ({
  isReCaptureValid,
});

export const Recaptcha = connect(RecaptchaMapStateToProps)(RecaptchaField);

export const PromoCodeField = props => (
  <Field
    name="voucherCode"
    component={TextInput}
    type="text"
    attrs={{ autoComplete: 'voucherCode' }}
    labelId="promotion.label"
    {...props}
  />
);

PromoCodeField.propTypes = colorPropTypes;
PromoCodeField.defaultProps = {
  ...defaultColorProps,
  textColor: colors.BUTTERFLY_BUSH,
  backgroundColor: colors.ALICE_BLUE,
};
